/**
 * MagicLinkVerify Page
 * Handles customer magic link verification and redirects to their portal
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { verifyMagicLink } from '../../services/publicBookingApi';

type VerifyStatus = 'verifying' | 'success' | 'error' | 'expired';

export function MagicLinkVerifyPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const safeRedirect = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;
  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [redirectUrl, setRedirectUrl] = useState<string>('');

  useEffect(() => {
    async function verify() {
      if (!token) {
        setStatus('error');
        setErrorMessage('Enlace inválido');
        return;
      }

      try {
        const result = await verifyMagicLink(token);

        if (result.success) {
          setStatus('success');
          // Store customer session if provided
          if (result.customerToken) {
            sessionStorage.setItem('customerToken', result.customerToken);
          }
          // Set redirect URL for the tracking page
          // The /track page is served by the API and will show a lookup form
          setRedirectUrl(safeRedirect || '/track');
        } else {
          if (result.error?.includes('expirado') || result.error?.includes('expired')) {
            setStatus('expired');
          } else {
            setStatus('error');
          }
          setErrorMessage(result.error || 'Error al verificar el enlace');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Error al verificar el enlace');
      }
    }

    verify();
  }, [token]);

  const handleContinue = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Verificando enlace...</h1>
              <p className="text-gray-500">Por favor espere mientras verificamos su acceso.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Acceso Verificado</h1>
              <p className="text-gray-500 mb-6">
                Su identidad ha sido verificada correctamente.
              </p>
              {redirectUrl && (
                <Button onClick={handleContinue} className="w-full">
                  Ver Estado de mi Cita
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-yellow-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Enlace Expirado</h1>
              <p className="text-gray-500 mb-6">
                Este enlace de acceso ha expirado. Por favor solicite un nuevo enlace.
              </p>
              <Link to="/booking">
                <Button variant="outline" className="w-full">
                  Reservar Nueva Cita
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Error de Verificación</h1>
              <p className="text-gray-500 mb-6">
                {errorMessage || 'No se pudo verificar el enlace. Por favor intentelo de nuevo.'}
              </p>
              <Link to="/booking">
                <Button variant="outline" className="w-full">
                  Reservar Nueva Cita
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
